import './UserName.scss';
import React, { useContext } from 'react';
import clsx from 'clsx';
import { FloatingPosition } from '@mantine/core';
import { MdPerson } from 'react-icons/md';
import UserHoverCard from '@molecules/UserHoverCard/UserHoverCard';
import UserAvatar from '@molecules/UserAvatar/UserAvatar';
import { useAvatarMeta } from '@molecules/UserAvatar/useAvatarMeta';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';

export interface UserNameProps {
    /** Ключ элемента адресной книги. Без него — просто текст без hover. number приводится к строке. */
    userKey?: string | number | null;
    /** Отображаемое имя (ФИО/инициалы). */
    name?: string | null;
    /** Позиция hover-карточки относительно имени. */
    position?: FloatingPosition;
    className?: string;
    /** Круглая иконка-человек слева от имени (как в поле документа и ячейках гридов). */
    withIcon?: boolean;
    /** Реальный аватар (UserAvatar) слева вместо обобщённой иконки; без фото — инициалы. */
    avatar?: boolean;
    /** z-index карточки — поднять внутри модалки, иначе карточка откроется под ней. */
    hoverCardZIndex?: number;
    /** Тип элемента адресной книги для аватара: не 'user' — значок группы вместо инициалов. */
    itemType?: string;
}

/**
 * Имя пользователя как текст с hover-карточкой контактов (UserHoverCard). Если ключа нет —
 * рендерит обычный текст без подчёркивания и без affordance (не обманываем пользователя).
 * Если ключ есть — на hover появляется пунктирное подчёркивание (сигнал «есть доп. информация»,
 * не путается со ссылкой), курсор остаётся обычным. Источник правды о ФИО/должности/контактах —
 * сама карточка; здесь показываем переданный name (часто инициалы). При withIcon слева — круглая
 * иконка-человек (единый «тег пользователя» для полей документа и ячеек гридов).
 */
const UserName: React.FC<UserNameProps> = ({
    userKey,
    name,
    position = 'top',
    className,
    withIcon = false,
    avatar = false,
    hoverCardZIndex,
    itemType,
}) => {
    const runtime = useContext(DocumentRuntimeContext);
    const key = userKey != null && userKey !== '' ? String(userKey) : undefined;
    const interactiveKey = runtime.mode === 'supportPreview' ? undefined : key;
    const text = name ?? '';
    // В avatar-режиме батч-подгружаем метаданные (hasImage/photoVersion): пока не
    // загружено — hasImage=false (инициалы, без запроса фото и без 404-шторма).
    const meta = useAvatarMeta(avatar ? interactiveKey : undefined);

    const inner = interactiveKey ? (
        <UserHoverCard userKey={interactiveKey} fallbackName={text} position={position} zIndex={hoverCardZIndex} inline>
            <span className={clsx('user-name', 'user-name--interactive', className)}>{text}</span>
        </UserHoverCard>
    ) : (
        <span className={clsx('user-name', className)}>{text}</span>
    );

    if (!withIcon && !avatar) {
        return inner;
    }

    return (
        <span className="user-name-tag">
            {avatar ? (
                <UserAvatar
                    userKey={interactiveKey}
                    name={text}
                    type={itemType}
                    hasImage={meta?.hasImage ?? false}
                    photoVersion={meta?.photoVersion ?? undefined}
                    size={22}
                    className="user-name-tag__avatar"
                />
            ) : (
                <span className="user-name-tag__ic" aria-hidden>
                    <MdPerson size={12} />
                </span>
            )}
            {inner}
        </span>
    );
};

export default UserName;
